import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { calculateRenewalDate } from '../utils/paymentUtils';

/**
 * Admin Service
 * Functions for admin-only operations
 * All functions verify admin role before executing
 * Includes audit logging for compliance
 */

/**
 * Verify user is admin before allowing operation
 */
async function verifyAdmin(userId) {
  if (!isSupabaseConfigured) {
    return { isAdmin: false, error: 'Supabase not configured' };
  }

  if (!userId) {
    return { isAdmin: false, error: 'User ID required' };
  }

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', userId)
      .single();

    if (error) throw error;
    
    if (data?.role !== 'admin') {
      return { isAdmin: false, error: 'Unauthorized: Admin access required' };
    }

    return { isAdmin: true };
  } catch (error) {
    console.error('Error verifying admin:', error);
    return { isAdmin: false, error: error.message };
  }
}

/**
 * Get client IP address and user agent
 */
function getClientInfo() {
  // Note: In a browser environment, we can't get the real IP
  // This would need to be done server-side in a real application
  // For now, we'll capture what we can from the browser
  return {
    ipAddress: null, // Would be set server-side
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  };
}

/**
 * Check rate limit for an operation
 */
async function checkRateLimit(adminUserId, operationType, maxAttempts, windowMinutes) {
  try {
    const { data, error } = await supabase.rpc('check_rate_limit', {
      p_user_id: adminUserId,
      p_operation_type: operationType,
      p_max_attempts: maxAttempts,
      p_window_minutes: windowMinutes,
    });

    if (error) {
      console.error('Error checking rate limit:', error);
      // Fail open - allow operation if rate limit check fails
      return { allowed: true, error: null };
    }

    return { allowed: data === true, error: data === false ? 'Rate limit exceeded' : null };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // Fail open - allow operation if rate limit check fails
    return { allowed: true, error: null };
  }
}

/**
 * Log admin action to audit_log table with IP and user agent
 */
async function logAdminAction(adminUserId, actionData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    const clientInfo = getClientInfo();
    
    // Use RPC function to log action (bypasses RLS for audit logging)
    const { data, error } = await supabase.rpc('log_admin_action', {
      p_admin_user_id: adminUserId,
      p_action_type: actionData.actionType,
      p_resource_type: actionData.resourceType,
      p_resource_id: actionData.resourceId || null,
      p_target_user_id: actionData.targetUserId || null,
      p_details: actionData.details || {},
      p_success: actionData.success !== false,
      p_error_message: actionData.errorMessage || null,
      p_ip_address: clientInfo.ipAddress,
      p_user_agent: clientInfo.userAgent,
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error logging admin action:', error);
    // Don't fail the operation if logging fails, but log the error
    return { success: false, error: error.message };
  }
}

/**
 * Get user by email (for looking up user ID from email)
 * Includes rate limiting to prevent email enumeration
 */
export async function getUserByEmail(adminUserId, email) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for email lookups (10 per minute)
  const rateLimitCheck = await checkRateLimit(adminUserId, 'email_lookup', 10, 1);
  if (!rateLimitCheck.allowed) {
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'user_profile',
      details: { email, operation: 'email_lookup' },
      success: false,
      errorMessage: 'Email lookup rate limit exceeded (10 per minute)',
    });
    return { success: false, error: 'Rate limit exceeded. Please wait before looking up more users.' };
  }

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, email, full_name')
      .eq('email', email)
      .single();

    const found = !error && data !== null;

    // Record the lookup attempt with rate limiting
    try {
      await supabase.rpc('record_user_lookup', {
        p_admin_user_id: adminUserId,
        p_email: email,
        p_found: found,
      });
    } catch (lookupError) {
      // Don't fail the operation if lookup logging fails
      console.warn('Failed to record user lookup:', lookupError);
    }

    if (error) {
      // Still record failed lookup
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching user by email:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get all clients/users
 */
export async function getAllClients(adminUserId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error, data: [] };
  }

  // Check rate limit for admin operations (100 per minute)
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'user_profiles',
      success: false,
      errorMessage: 'Admin operation rate limit exceeded (100 per minute)',
    });
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.', data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Log the action
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'user_profiles',
      details: { count: data?.length || 0 },
    });

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching clients:', error);
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'user_profiles',
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Get all payments (admin view with user info)
 * @param {string} adminUserId - Admin user ID
 * @param {Object} options - Pagination and filter options
 * @param {number} options.page - Page number (1-indexed, default: 1)
 * @param {number} options.pageSize - Number of items per page (default: 20)
 * @param {string} options.status - Filter by status (optional)
 * @param {string} options.provider - Filter by provider (optional)
 * @param {string} options.startDate - Filter by start date (ISO string, optional)
 * @param {string} options.endDate - Filter by end date (ISO string, optional)
 * @param {string} options.userId - Filter by specific user ID (optional)
 * @returns {Promise<Object>} Object with data array and pagination metadata
 */
export async function getAllPayments(adminUserId, options = {}) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { 
      success: false, 
      error: adminCheck.error, 
      data: [],
      pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 }
    };
  }

  const page = options.page || 1;
  const pageSize = options.pageSize || 20;
  const offset = (page - 1) * pageSize;

  try {
    // Build base query with count
    let query = supabase
      .from('payments')
      .select(`
        *,
        user_profiles:user_id (
          id,
          email,
          full_name
        )
      `, { count: 'exact' });

    // Apply filters
    if (options.status) {
      query = query.eq('status', options.status);
    }
    if (options.provider) {
      query = query.eq('payment_provider', options.provider);
    }
    if (options.userId) {
      query = query.eq('user_id', options.userId);
    }
    if (options.startDate) {
      query = query.gte('created_at', options.startDate);
    }
    if (options.endDate) {
      query = query.lte('created_at', options.endDate);
    }

    // Apply ordering
    query = query.order('created_at', { ascending: false });

    // Apply pagination
    query = query.range(offset, offset + pageSize - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'payments',
      details: { 
        count: data?.length || 0,
        page,
        pageSize,
        total,
      },
    });

    return { 
      success: true, 
      data: data || [],
      pagination: {
        page,
        pageSize,
        total,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      }
    };
  } catch (error) {
    console.error('Error fetching payments:', error);
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'payments',
      success: false,
      errorMessage: error.message,
    });
    return { 
      success: false, 
      error: error.message, 
      data: [],
      pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 }
    };
  }
}

/**
 * Create a payment for a user
 * For Stripe payments, this creates a pending payment record.
 * The Stripe Checkout session should be created separately when the client initiates payment.
 */
export async function createPayment(adminUserId, paymentData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    // Prepare metadata for Stripe payments
    const metadata = paymentData.metadata || {};
    
    // For Stripe payments, ensure status is pending and add Stripe-specific metadata
    if (paymentData.provider === 'stripe') {
      metadata.stripe_ready = true;
      metadata.created_by_admin = true;
      metadata.created_at = new Date().toISOString();
      // Note: stripe_session_id will be set when checkout session is created
    }

    // Calculate renewal date for subscriptions
    let renewalDate = null;
    let subscriptionStartDate = null;
    
    if (paymentData.paymentType === 'subscription' && paymentData.renewalPeriod) {
      try {
        renewalDate = calculateRenewalDate(
          paymentData.renewalPeriod,
          paymentData.customMonths || null
        );
        
        // For completed subscriptions, set start date to now
        if (paymentData.status === 'completed') {
          subscriptionStartDate = new Date().toISOString();
        }
      } catch (error) {
        console.error('Error calculating renewal date:', error);
        // Continue without renewal date if calculation fails
      }
    }

    const { data, error } = await supabase
      .from('payments')
      .insert({
        user_id: paymentData.userId,
        payment_provider: paymentData.provider,
        provider_payment_id: paymentData.providerPaymentId || `admin-${Date.now()}`,
        amount: paymentData.amount,
        currency: paymentData.currency || 'USD',
        status: paymentData.provider === 'stripe' ? 'pending' : (paymentData.status || 'pending'),
        payment_type: paymentData.paymentType,
        description: paymentData.description,
        invoice_url: paymentData.invoiceUrl,
        receipt_url: paymentData.receiptUrl,
        metadata: metadata,
        // Stripe-specific fields (will be null initially, set when session is created)
        stripe_session_id: paymentData.stripeSessionId || null,
        stripe_payment_intent_id: paymentData.stripePaymentIntentId || null,
        // Subscription renewal fields
        renewal_period: paymentData.paymentType === 'subscription' ? paymentData.renewalPeriod : null,
        renewal_interval_months: paymentData.paymentType === 'subscription' && paymentData.renewalPeriod === 'custom' ? paymentData.customMonths : null,
        renewal_date: renewalDate ? renewalDate.toISOString() : null,
        subscription_start_date: subscriptionStartDate,
      })
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'payment',
      resourceId: data.id,
      targetUserId: paymentData.userId,
      details: {
        amount: paymentData.amount,
        provider: paymentData.provider,
        status: data.status,
        stripePayment: paymentData.provider === 'stripe',
      },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error creating payment:', error);
    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'payment',
      targetUserId: paymentData.userId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Update payment with Stripe session information
 * Called when a Stripe Checkout session is created for a payment
 */
export async function updatePaymentWithStripeSession(adminUserId, paymentId, sessionData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    const updates = {
      stripe_session_id: sessionData.sessionId,
      metadata: {
        ...sessionData.metadata,
        stripe_checkout_url: sessionData.checkoutUrl,
        stripe_session_created_at: new Date().toISOString(),
      },
    };

    const { data, error } = await supabase
      .from('payments')
      .update(updates)
      .eq('id', paymentId)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'payment',
      resourceId: paymentId,
      details: {
        action: 'stripe_session_created',
        sessionId: sessionData.sessionId,
      },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error updating payment with Stripe session:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update payment status
 */
export async function updatePayment(adminUserId, paymentId, updates) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    // Get current payment to log target user
    const { data: currentPayment } = await supabase
      .from('payments')
      .select('user_id')
      .eq('id', paymentId)
      .single();

    const { data, error } = await supabase
      .from('payments')
      .update(updates)
      .eq('id', paymentId)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'payment',
      resourceId: paymentId,
      targetUserId: currentPayment?.user_id,
      details: updates,
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error updating payment:', error);
    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'payment',
      resourceId: paymentId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Delete a payment
 */
export async function deletePayment(adminUserId, paymentId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    // Get payment info before deletion for audit log
    const { data: paymentInfo } = await supabase
      .from('payments')
      .select('user_id, amount')
      .eq('id', paymentId)
      .single();

    const { error } = await supabase
      .from('payments')
      .delete()
      .eq('id', paymentId);

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'payment',
      resourceId: paymentId,
      targetUserId: paymentInfo?.user_id,
      details: { amount: paymentInfo?.amount },
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting payment:', error);
    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'payment',
      resourceId: paymentId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Create a notice (user-specific or global)
 */
export async function createNotice(adminUserId, noticeData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    const { data, error } = await supabase
      .from('notices')
      .insert({
        user_id: noticeData.userId || null,
        title: noticeData.title,
        message: noticeData.message,
        type: noticeData.type || 'info',
        is_global: noticeData.isGlobal || false,
        action_url: noticeData.actionUrl,
        expires_at: noticeData.expiresAt,
      })
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'notice',
      resourceId: data.id,
      targetUserId: noticeData.userId || null,
      details: {
        title: noticeData.title,
        isGlobal: noticeData.isGlobal || false,
        type: noticeData.type,
      },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error creating notice:', error);
    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'notice',
      targetUserId: noticeData.userId || null,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Get all notices
 */
export async function getAllNotices(adminUserId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error, data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('notices')
      .select(`
        *,
        user_profiles:user_id (
          id,
          email,
          full_name
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'notices',
      details: { count: data?.length || 0 },
    });

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching notices:', error);
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'notices',
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Update a notice
 */
export async function updateNotice(adminUserId, noticeId, updates) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    const { data: currentNotice } = await supabase
      .from('notices')
      .select('user_id')
      .eq('id', noticeId)
      .single();

    const { data, error } = await supabase
      .from('notices')
      .update(updates)
      .eq('id', noticeId)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'notice',
      resourceId: noticeId,
      targetUserId: currentNotice?.user_id,
      details: updates,
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error updating notice:', error);
    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'notice',
      resourceId: noticeId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Delete a notice
 */
export async function deleteNotice(adminUserId, noticeId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    const { data: noticeInfo } = await supabase
      .from('notices')
      .select('user_id, title')
      .eq('id', noticeId)
      .single();

    const { error } = await supabase
      .from('notices')
      .delete()
      .eq('id', noticeId);

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'notice',
      resourceId: noticeId,
      targetUserId: noticeInfo?.user_id,
      details: { title: noticeInfo?.title },
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting notice:', error);
    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'notice',
      resourceId: noticeId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Get all user programs
 */
export async function getAllPrograms(adminUserId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error, data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('user_programs')
      .select(`
        *,
        user_profiles:user_id (
          id,
          email,
          full_name
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'user_programs',
      details: { count: data?.length || 0 },
    });

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching programs:', error);
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'user_programs',
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Create or update a user program
 */
export async function upsertProgram(adminUserId, programData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    const programPayload = {
      user_id: programData.userId,
      program_name: programData.programName,
      program_type: programData.programType,
      status: programData.status || 'active',
      start_date: programData.startDate,
      end_date: programData.endDate,
      coach_notes: programData.coachNotes,
      program_data: programData.programData || {},
    };

    let result;
    if (programData.id) {
      // Update existing
      const { data, error } = await supabase
        .from('user_programs')
        .update(programPayload)
        .eq('id', programData.id)
        .select()
        .single();
      result = { data, error };
    } else {
      // Create new
      const { data, error } = await supabase
        .from('user_programs')
        .insert(programPayload)
        .select()
        .single();
      result = { data, error };
    }

    if (result.error) throw result.error;

    await logAdminAction(adminUserId, {
      actionType: programData.id ? 'update' : 'create',
      resourceType: 'user_program',
      resourceId: result.data.id,
      targetUserId: programData.userId,
      details: {
        programName: programData.programName,
        programType: programData.programType,
        status: programData.status,
      },
    });

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error upserting program:', error);
    await logAdminAction(adminUserId, {
      actionType: programData.id ? 'update' : 'create',
      resourceType: 'user_program',
      resourceId: programData.id,
      targetUserId: programData.userId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Delete a program
 */
export async function deleteProgram(adminUserId, programId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    const { data: programInfo } = await supabase
      .from('user_programs')
      .select('user_id, program_name')
      .eq('id', programId)
      .single();

    const { error } = await supabase
      .from('user_programs')
      .delete()
      .eq('id', programId);

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'user_program',
      resourceId: programId,
      targetUserId: programInfo?.user_id,
      details: { programName: programInfo?.program_name },
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting program:', error);
    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'user_program',
      resourceId: programId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Get all progress entries for a specific user (admin view)
 */
export async function getAllProgressEntries(adminUserId, userId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error, data: [] };
  }

  try {
    let query = supabase
      .from('progress_entries')
      .select('*')
      .order('entry_date', { ascending: false });

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query;

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'progress_entries',
      targetUserId: userId,
      details: { count: data?.length || 0 },
    });

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching progress entries:', error);
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'progress_entries',
      targetUserId: userId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Create a progress entry for a user
 */
export async function createProgressEntry(adminUserId, entryData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    const { data, error } = await supabase
      .from('progress_entries')
      .insert({
        user_id: entryData.userId,
        entry_type: entryData.entryType,
        entry_date: entryData.entryDate,
        data: entryData.data || {},
        notes: entryData.notes || null,
      })
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'progress_entry',
      resourceId: data.id,
      targetUserId: entryData.userId,
      details: {
        entryType: entryData.entryType,
        entryDate: entryData.entryDate,
      },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error creating progress entry:', error);
    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'progress_entry',
      targetUserId: entryData.userId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Update user profile (including role - admin only)
 * Includes rate limiting for role changes (5 per hour)
 */
export async function updateUserProfile(adminUserId, userId, updates) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for role changes if role is being updated
  if (updates.role) {
    const rateLimitCheck = await checkRateLimit(adminUserId, 'role_change', 5, 60);
    if (!rateLimitCheck.allowed) {
      await logAdminAction(adminUserId, {
        actionType: 'update',
        resourceType: 'user_profile',
        resourceId: userId,
        targetUserId: userId,
        details: updates,
        success: false,
        errorMessage: 'Role change rate limit exceeded (5 per hour)',
      });
      return { success: false, error: 'Rate limit exceeded for role changes. Please wait before changing more roles.' };
    }
  }

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'user_profile',
      resourceId: userId,
      targetUserId: userId,
      details: updates,
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error updating user profile:', error);
    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'user_profile',
      resourceId: userId,
      targetUserId: userId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Get admin dashboard summary
 */
export async function getAdminSummary(adminUserId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    const [clientsResult, paymentsResult, noticesResult, programsResult] = await Promise.all([
      getAllClients(adminUserId),
      getAllPayments(adminUserId),
      getAllNotices(adminUserId),
      getAllPrograms(adminUserId),
    ]);

    const clients = clientsResult.data || [];
    const payments = paymentsResult.data || [];
    const notices = noticesResult.data || [];
    const programs = programsResult.data || [];

    const summary = {
      totalClients: clients.length,
      activeClients: clients.filter(c => c.role === 'user').length,
      adminCount: clients.filter(c => c.role === 'admin').length,
      totalPayments: payments.length,
      pendingPayments: payments.filter(p => p.status === 'pending').length,
      completedPayments: payments.filter(p => p.status === 'completed').length,
      totalRevenue: payments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
      unreadNotices: notices.filter(n => !n.is_read).length,
      globalNotices: notices.filter(n => n.is_global).length,
      activePrograms: programs.filter(p => p.status === 'active').length,
      totalPrograms: programs.length,
    };

    return { success: true, data: summary };
  } catch (error) {
    console.error('Error fetching admin summary:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get audit log entries
 */
export async function getAuditLog(adminUserId, filters = {}) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error, data: [] };
  }

  try {
    let query = supabase
      .from('audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(filters.limit || 100);

    if (filters.actionType) {
      query = query.eq('action_type', filters.actionType);
    }

    if (filters.resourceType) {
      query = query.eq('resource_type', filters.resourceType);
    }

    if (filters.startDate) {
      query = query.gte('created_at', filters.startDate);
    }

    if (filters.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching audit log:', error);
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Create a workout for a plan
 */
export async function createWorkout(adminUserId, planId, workoutData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    const { data, error } = await supabase
      .from('plan_workouts')
      .insert({
        program_id: planId,
        workout_name: workoutData.workoutName,
        workout_type: workoutData.workoutType || 'strength',
        scheduled_date: workoutData.scheduledDate || null,
        week_number: workoutData.weekNumber || null,
        day_number: workoutData.dayNumber || null,
        duration_minutes: workoutData.durationMinutes || null,
        rest_day: workoutData.restDay || false,
        instructions: workoutData.instructions || null,
        notes: workoutData.notes || null,
        exercise_order: workoutData.exerciseOrder || [],
      })
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'plan_workout',
      resourceId: data.id,
      details: { workoutName: workoutData.workoutName, planId },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error creating workout:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Create an exercise
 */
export async function createExercise(adminUserId, exerciseData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    const { data, error } = await supabase
      .from('plan_exercises')
      .insert({
        program_id: exerciseData.programId || null,
        exercise_name: exerciseData.exerciseName,
        description: exerciseData.description || null,
        muscle_groups: exerciseData.muscleGroups || [],
        equipment_type: exerciseData.equipmentType || 'bodyweight',
        video_url: exerciseData.videoUrl || null,
        image_urls: exerciseData.imageUrls || [],
        instructions: exerciseData.instructions || null,
        form_cues: exerciseData.formCues || [],
        default_sets: exerciseData.defaultSets || null,
        default_reps: exerciseData.defaultReps || null,
        default_weight: exerciseData.defaultWeight || null,
        default_rest_seconds: exerciseData.defaultRestSeconds || null,
        is_global: exerciseData.isGlobal || false,
      })
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'plan_exercise',
      resourceId: data.id,
      details: { exerciseName: exerciseData.exerciseName },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error creating exercise:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Create a meal plan
 */
export async function createMealPlan(adminUserId, planId, mealData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    const { data, error } = await supabase
      .from('plan_meals')
      .insert({
        program_id: planId,
        meal_name: mealData.mealName,
        meal_type: mealData.mealType,
        scheduled_date: mealData.scheduledDate || null,
        week_number: mealData.weekNumber || null,
        day_number: mealData.dayNumber || null,
        meal_order: mealData.mealOrder || 1,
        recipe_id: mealData.recipeId || null,
        image_url: mealData.imageUrl || null,
        description: mealData.description || null,
        ingredients: mealData.ingredients || [],
        instructions: mealData.instructions || null,
        macros: mealData.macros || {},
        serving_size: mealData.servingSize || null,
        prep_time_minutes: mealData.prepTimeMinutes || null,
        cook_time_minutes: mealData.cookTimeMinutes || null,
        notes: mealData.notes || null,
      })
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'plan_meal',
      resourceId: data.id,
      details: { mealName: mealData.mealName, planId },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error creating meal plan:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Configure plan features
 */
export async function configurePlanFeatures(adminUserId, planId, config) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    // Check if configuration exists
    const { data: existing } = await supabase
      .from('plan_configurations')
      .select('id')
      .eq('program_id', planId)
      .single();

    let result;
    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('plan_configurations')
        .update({
          enable_training: config.enableTraining !== undefined ? config.enableTraining : true,
          enable_nutrition: config.enableNutrition !== undefined ? config.enableNutrition : true,
          enable_progress_tracking: config.enableProgressTracking !== undefined ? config.enableProgressTracking : true,
          enable_messaging: config.enableMessaging !== undefined ? config.enableMessaging : true,
          enable_resources: config.enableResources !== undefined ? config.enableResources : true,
          enable_checkpoints: config.enableCheckpoints !== undefined ? config.enableCheckpoints : true,
          show_workout_calendar: config.showWorkoutCalendar !== undefined ? config.showWorkoutCalendar : true,
          show_exercise_library: config.showExerciseLibrary !== undefined ? config.showExerciseLibrary : true,
          show_meal_plans: config.showMealPlans !== undefined ? config.showMealPlans : true,
          show_macro_tracker: config.showMacroTracker !== undefined ? config.showMacroTracker : true,
          show_recipe_library: config.showRecipeLibrary !== undefined ? config.showRecipeLibrary : true,
          show_progress_photos: config.showProgressPhotos !== undefined ? config.showProgressPhotos : true,
          show_measurements: config.showMeasurements !== undefined ? config.showMeasurements : true,
          show_check_ins: config.showCheckIns !== undefined ? config.showCheckIns : true,
          custom_fields: config.customFields || {},
          access_level: config.accessLevel || 'standard',
          template_id: config.templateId || null,
        })
        .eq('id', existing.id)
        .select()
        .single();
      result = { data, error };
    } else {
      // Create new
      const { data, error } = await supabase
        .from('plan_configurations')
        .insert({
          program_id: planId,
          enable_training: config.enableTraining !== undefined ? config.enableTraining : true,
          enable_nutrition: config.enableNutrition !== undefined ? config.enableNutrition : true,
          enable_progress_tracking: config.enableProgressTracking !== undefined ? config.enableProgressTracking : true,
          enable_messaging: config.enableMessaging !== undefined ? config.enableMessaging : true,
          enable_resources: config.enableResources !== undefined ? config.enableResources : true,
          enable_checkpoints: config.enableCheckpoints !== undefined ? config.enableCheckpoints : true,
          show_workout_calendar: config.showWorkoutCalendar !== undefined ? config.showWorkoutCalendar : true,
          show_exercise_library: config.showExerciseLibrary !== undefined ? config.showExerciseLibrary : true,
          show_meal_plans: config.showMealPlans !== undefined ? config.showMealPlans : true,
          show_macro_tracker: config.showMacroTracker !== undefined ? config.showMacroTracker : true,
          show_recipe_library: config.showRecipeLibrary !== undefined ? config.showRecipeLibrary : true,
          show_progress_photos: config.showProgressPhotos !== undefined ? config.showProgressPhotos : true,
          show_measurements: config.showMeasurements !== undefined ? config.showMeasurements : true,
          show_check_ins: config.showCheckIns !== undefined ? config.showCheckIns : true,
          custom_fields: config.customFields || {},
          access_level: config.accessLevel || 'standard',
          template_id: config.templateId || null,
        })
        .select()
        .single();
      result = { data, error };
    }

    if (result.error) throw result.error;

    await logAdminAction(adminUserId, {
      actionType: existing ? 'update' : 'create',
      resourceType: 'plan_configuration',
      resourceId: result.data.id,
      details: { planId, config },
    });

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error configuring plan features:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get all blogs (admin view - includes unpublished)
 * @param {string} adminUserId - Admin user ID
 * @returns {Promise<{success: boolean, data: Array, error?: string}>}
 */
export async function getAllBlogs(adminUserId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error, data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('blogs')
      .select(`
        *,
        user_profiles:author_id (
          id,
          full_name,
          email
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'blogs',
      details: { count: data?.length || 0 },
    });

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching blogs:', error);
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'blogs',
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Get a single blog by ID (admin view - any status)
 * @param {string} adminUserId - Admin user ID
 * @param {string} blogId - Blog ID
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function getBlogById(adminUserId, blogId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    const { data, error } = await supabase
      .from('blogs')
      .select(`
        *,
        user_profiles:author_id (
          id,
          full_name,
          email
        )
      `)
      .eq('id', blogId)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching blog:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Create a new blog
 * @param {string} adminUserId - Admin user ID
 * @param {Object} blogData - Blog data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function createBlog(adminUserId, blogData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    // Validate required fields
    if (!blogData.title || !blogData.content) {
      return { success: false, error: 'Title and content are required' };
    }

    // Import SEO utilities
    const { generateSlug, stripHtml, generateMetaDescription, extractKeywords, calculateReadTime, generateUniqueSlug } = await import('../utils/seoUtils');

    // Generate slug
    const baseSlug = blogData.slug || generateSlug(blogData.title);
    
    // Check if slug exists
    const checkSlugExists = async (slug) => {
      const { data } = await supabase
        .from('blogs')
        .select('id')
        .eq('slug', slug)
        .single();
      return !!data;
    };

    const slug = await generateUniqueSlug(baseSlug, checkSlugExists);

    // Generate SEO content
    const seoContent = stripHtml(blogData.content);
    const readTime = calculateReadTime(blogData.content);
    const metaDescription = blogData.meta_description || generateMetaDescription(blogData.content);
    const metaKeywords = blogData.meta_keywords || extractKeywords(blogData.content);

    // Prepare blog data
    const insertData = {
      title: blogData.title,
      slug,
      excerpt: blogData.excerpt || null,
      content: blogData.content,
      seo_content: seoContent,
      header_image_url: blogData.header_image_url || null,
      meta_title: blogData.meta_title || blogData.title,
      meta_description: metaDescription,
      meta_keywords: Array.isArray(metaKeywords) ? metaKeywords : [],
      category: blogData.category || null,
      tags: Array.isArray(blogData.tags) ? blogData.tags : [],
      is_published: blogData.is_published || false,
      published_at: blogData.is_published ? new Date().toISOString() : null,
      author_id: adminUserId,
      read_time_minutes: readTime,
    };

    const { data, error } = await supabase
      .from('blogs')
      .insert(insertData)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'blog',
      resourceId: data.id,
      details: {
        title: blogData.title,
        is_published: blogData.is_published || false,
      },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error creating blog:', error);
    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'blog',
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Update a blog
 * @param {string} adminUserId - Admin user ID
 * @param {string} blogId - Blog ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function updateBlog(adminUserId, blogId, updates) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    // Import SEO utilities
    const { stripHtml, generateMetaDescription, extractKeywords, calculateReadTime } = await import('../utils/seoUtils');

    // Prepare update object
    const updateData = {};

    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.slug !== undefined) updateData.slug = updates.slug;
    if (updates.excerpt !== undefined) updateData.excerpt = updates.excerpt;
    if (updates.content !== undefined) {
      updateData.content = updates.content;
      // Recalculate SEO fields when content changes
      updateData.seo_content = stripHtml(updates.content);
      updateData.read_time_minutes = calculateReadTime(updates.content);
      if (!updates.meta_description) {
        updateData.meta_description = generateMetaDescription(updates.content);
      }
      if (!updates.meta_keywords) {
        updateData.meta_keywords = extractKeywords(updates.content);
      }
    }
    if (updates.header_image_url !== undefined) updateData.header_image_url = updates.header_image_url;
    if (updates.meta_title !== undefined) updateData.meta_title = updates.meta_title;
    if (updates.meta_description !== undefined) updateData.meta_description = updates.meta_description;
    if (updates.meta_keywords !== undefined) {
      updateData.meta_keywords = Array.isArray(updates.meta_keywords) ? updates.meta_keywords : [];
    }
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.tags !== undefined) {
      updateData.tags = Array.isArray(updates.tags) ? updates.tags : [];
    }
    if (updates.is_published !== undefined) {
      updateData.is_published = updates.is_published;
      // Set published_at when publishing for the first time
      if (updates.is_published) {
        // Check if already published
        const { data: currentBlog } = await supabase
          .from('blogs')
          .select('published_at')
          .eq('id', blogId)
          .single();
        
        if (!currentBlog?.published_at) {
          updateData.published_at = new Date().toISOString();
        }
      }
    }

    const { data, error } = await supabase
      .from('blogs')
      .update(updateData)
      .eq('id', blogId)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'blog',
      resourceId: blogId,
      details: Object.keys(updates),
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error updating blog:', error);
    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'blog',
      resourceId: blogId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Delete a blog (soft delete via is_published = false)
 * @param {string} adminUserId - Admin user ID
 * @param {string} blogId - Blog ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteBlog(adminUserId, blogId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    // Soft delete by setting is_published to false
    const { error } = await supabase
      .from('blogs')
      .update({ is_published: false })
      .eq('id', blogId);

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'blog',
      resourceId: blogId,
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting blog:', error);
    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'blog',
      resourceId: blogId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Publish or unpublish a blog
 * @param {string} adminUserId - Admin user ID
 * @param {string} blogId - Blog ID
 * @param {boolean} publish - True to publish, false to unpublish
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function publishBlog(adminUserId, blogId, publish = true) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  try {
    const updateData = {
      is_published: publish,
    };

    // Set published_at when publishing for the first time
    if (publish) {
      const { data: currentBlog } = await supabase
        .from('blogs')
        .select('published_at')
        .eq('id', blogId)
        .single();

      if (!currentBlog?.published_at) {
        updateData.published_at = new Date().toISOString();
      }
    }

    const { data, error } = await supabase
      .from('blogs')
      .update(updateData)
      .eq('id', blogId)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'blog',
      resourceId: blogId,
      details: { action: publish ? 'publish' : 'unpublish' },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error publishing/unpublishing blog:', error);
    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'blog',
      resourceId: blogId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Get all contacts (admin view)
 * @param {string} adminUserId - Admin user ID
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Filter by status
 * @param {number} filters.limit - Number of results
 * @param {number} filters.offset - Pagination offset
 * @returns {Promise<{success: boolean, data: Array, error?: string, count?: number}>}
 */
export async function getAllContacts(adminUserId, filters = {}) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error, data: [] };
  }

  try {
    const {
      status,
      limit = 50,
      offset = 0,
      sortBy = 'created_at',
      order = 'desc',
    } = filters;

    // Build query
    let query = supabase
      .from('contacts')
      .select('*', { count: 'exact' });

    // Apply filters
    if (status) {
      query = query.eq('status', status);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: order === 'asc' });

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'contacts',
      details: { count: data?.length || 0, status },
    });

    return {
      success: true,
      data: data || [],
      count: count || 0,
    };
  } catch (error) {
    console.error('Error fetching contacts:', error);
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'contacts',
      success: false,
      errorMessage: error.message,
    });
    return {
      success: false,
      error: error.message || 'Failed to fetch contacts',
      data: [],
      count: 0,
    };
  }
}

/**
 * Update contact status
 * @param {string} adminUserId - Admin user ID
 * @param {string} contactId - Contact ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function updateContact(adminUserId, contactId, updates) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    const { data, error } = await supabase
      .from('contacts')
      .update(updates)
      .eq('id', contactId)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'contact',
      resourceId: contactId,
      details: updates,
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error updating contact:', error);
    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'contact',
      resourceId: contactId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Delete a contact
 * @param {string} adminUserId - Admin user ID
 * @param {string} contactId - Contact ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteContact(adminUserId, contactId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', contactId);

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'contact',
      resourceId: contactId,
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting contact:', error);
    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'contact',
      resourceId: contactId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

export default {
  getAllClients,
  getAllPayments,
  createPayment,
  updatePayment,
  deletePayment,
  createNotice,
  getAllNotices,
  updateNotice,
  deleteNotice,
  getAllPrograms,
  upsertProgram,
  deleteProgram,
  getAllProgressEntries,
  createProgressEntry,
  updateUserProfile,
  getAdminSummary,
  getAuditLog,
  createWorkout,
  createExercise,
  createMealPlan,
  configurePlanFeatures,
  getAllServices,
  createService,
  updateService,
  deleteService,
  getAllBlogs,
  getBlogById,
  createBlog,
  updateBlog,
  deleteBlog,
  publishBlog,
  getAllContacts,
  updateContact,
  deleteContact,
};

/**
 * Get all services (admin view - includes inactive)
 * @param {string} adminUserId - Admin user ID
 * @returns {Promise<{success: boolean, data: Array, error?: string}>}
 */
export async function getAllServices(adminUserId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error, data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'services',
      details: { count: data?.length || 0 },
    });

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching services:', error);
    await logAdminAction(adminUserId, {
      actionType: 'view',
      resourceType: 'services',
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Create a new service
 * @param {string} adminUserId - Admin user ID
 * @param {Object} serviceData - Service data
 * @returns {Promise<{success: boolean, data: Object|null, error?: string}>}
 */
export async function createService(adminUserId, serviceData) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    // Validate required fields
    if (!serviceData.name || !serviceData.tagline || !serviceData.icon_name || !serviceData.color || !serviceData.price || !serviceData.renewal_period) {
      return { success: false, error: 'Missing required fields: name, tagline, icon_name, color, price, and renewal_period are required' };
    }

    // Validate custom renewal period
    if (serviceData.renewal_period === 'custom') {
      if (!serviceData.renewal_interval_months || serviceData.renewal_interval_months < 1 || serviceData.renewal_interval_months > 24) {
        return { success: false, error: 'Custom renewal period requires renewal_interval_months between 1 and 24' };
      }
    }

    // Ensure features is an array
    const features = Array.isArray(serviceData.features) ? serviceData.features : [];

    const { data, error } = await supabase
      .from('services')
      .insert({
        name: serviceData.name,
        tagline: serviceData.tagline,
        description: serviceData.description || null,
        icon_name: serviceData.icon_name,
        color: serviceData.color,
        price: parseFloat(serviceData.price),
        currency: serviceData.currency || 'USD',
        renewal_period: serviceData.renewal_period,
        renewal_interval_months: serviceData.renewal_period === 'custom' ? parseInt(serviceData.renewal_interval_months) : null,
        features: features,
        max_students: serviceData.max_students || null,
        is_popular: serviceData.is_popular || false,
        is_active: serviceData.is_active !== undefined ? serviceData.is_active : true,
        display_order: serviceData.display_order || 0,
      })
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'service',
      resourceId: data.id,
      details: {
        name: serviceData.name,
        price: serviceData.price,
        renewal_period: serviceData.renewal_period,
      },
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error creating service:', error);
    await logAdminAction(adminUserId, {
      actionType: 'create',
      resourceType: 'service',
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Update a service
 * @param {string} adminUserId - Admin user ID
 * @param {string} serviceId - Service ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{success: boolean, data: Object|null, error?: string}>}
 */
export async function updateService(adminUserId, serviceId, updates) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    // Validate custom renewal period if being updated
    if (updates.renewal_period === 'custom') {
      if (!updates.renewal_interval_months || updates.renewal_interval_months < 1 || updates.renewal_interval_months > 24) {
        return { success: false, error: 'Custom renewal period requires renewal_interval_months between 1 and 24' };
      }
    } else if (updates.renewal_period && updates.renewal_period !== 'custom') {
      // Clear interval_months if not custom
      updates.renewal_interval_months = null;
    }

    // Prepare update object
    const updateData = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.tagline !== undefined) updateData.tagline = updates.tagline;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.icon_name !== undefined) updateData.icon_name = updates.icon_name;
    if (updates.color !== undefined) updateData.color = updates.color;
    if (updates.price !== undefined) updateData.price = parseFloat(updates.price);
    if (updates.currency !== undefined) updateData.currency = updates.currency;
    if (updates.renewal_period !== undefined) updateData.renewal_period = updates.renewal_period;
    if (updates.renewal_interval_months !== undefined) updateData.renewal_interval_months = updates.renewal_interval_months;
    if (updates.features !== undefined) updateData.features = Array.isArray(updates.features) ? updates.features : [];
    if (updates.max_students !== undefined) updateData.max_students = updates.max_students;
    if (updates.is_popular !== undefined) updateData.is_popular = updates.is_popular;
    if (updates.is_active !== undefined) updateData.is_active = updates.is_active;
    if (updates.display_order !== undefined) updateData.display_order = parseInt(updates.display_order);

    const { data, error } = await supabase
      .from('services')
      .update(updateData)
      .eq('id', serviceId)
      .select()
      .single();

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'service',
      resourceId: serviceId,
      details: updates,
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error updating service:', error);
    await logAdminAction(adminUserId, {
      actionType: 'update',
      resourceType: 'service',
      resourceId: serviceId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}

/**
 * Delete a service (soft delete via is_active)
 * @param {string} adminUserId - Admin user ID
 * @param {string} serviceId - Service ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteService(adminUserId, serviceId) {
  const adminCheck = await verifyAdmin(adminUserId);
  if (!adminCheck.isAdmin) {
    return { success: false, error: adminCheck.error };
  }

  // Check rate limit for admin operations
  const rateLimitCheck = await checkRateLimit(adminUserId, 'admin_operation', 100, 1);
  if (!rateLimitCheck.allowed) {
    return { success: false, error: 'Rate limit exceeded. Please wait before performing more operations.' };
  }

  try {
    // Soft delete by setting is_active to false
    const { error } = await supabase
      .from('services')
      .update({ is_active: false })
      .eq('id', serviceId);

    if (error) throw error;

    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'service',
      resourceId: serviceId,
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting service:', error);
    await logAdminAction(adminUserId, {
      actionType: 'delete',
      resourceType: 'service',
      resourceId: serviceId,
      success: false,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
}
